"use client";

import React, { useCallback, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toAbsoluteMediaUrl, unblurUrl } from "@/lib/media";
import { apiFetch } from "@/lib/api";
import BlurGate from "@/components/common/BlurGate";
import { useToast } from "@/components/ui/toast";
import VideoGalleryCard from "@/components/VideoGalleryCard";
import { scrubContactInfo } from "@/lib/scrubContact";

export type BaseFeed = { id: number | string; type: "post" | "image" | "video" | string; isUnsafeContent?: boolean; likeCount?: number; isLikedByMe?: boolean; title?: string | null };
export type VideoItem = BaseFeed & { type: "video"; thumbnailUrl?: string | null; durationSeconds?: number | null; viewCount?: number | null };
export type ImageItem = BaseFeed & { type: "image"; src?: string | null; description?: string | null };
export type PostItem  = BaseFeed & { type: "post"; snippet?: string | null; thumbnailUrl?: string | null };
export type FeedItem = VideoItem | ImageItem | PostItem | BaseFeed;

export default function FeedCard({ item, onOpenImage }: { item: FeedItem; onOpenImage?: (src: string, alt?: string, caption?: string) => void }) {
  const { status } = useSession();
  const authed = status === "authenticated";
  const { toast } = useToast();
  const [likes, setLikes] = useState<number | undefined>(typeof item.likeCount === 'number' ? item.likeCount : undefined);
  const [liked, setLiked] = useState<boolean | undefined>(typeof item.isLikedByMe === 'boolean' ? item.isLikedByMe : undefined);
  const [busy, setBusy] = useState(false);
  // Use Next proxy for auth-bearing actions
  const API_BASE = useMemo(() => "", []);

  const likePath = useMemo(() => {
    const id = encodeURIComponent(String(item.id));
    if (item.type === 'video') return `/api/video/v2/${id}/like`;
    if (item.type === 'post')  return `/api/post/${id}/like`;
    if (item.type === 'image') return `/api/image/${id}/like`;
    return '';
  }, [item]);

  const reportPath = useMemo(() => {
    const id = encodeURIComponent(String(item.id));
    if (item.type === 'video') return `/api/video/v2/${id}/report`;
    if (item.type === 'post')  return `/api/post/${id}/report`;
    if (item.type === 'image') return `/api/image/${id}/report`;
    return '';
  }, [item]);

  const toggleLike = useCallback(async () => {
    if (!likePath || busy) return;
    setBusy(true);
    const prevLiked = !!liked;
    const prevLikes = typeof likes === 'number' ? likes : 0;
    setLiked(!prevLiked);
    setLikes(prevLiked ? Math.max(0, prevLikes - 1) : prevLikes + 1);
    try {
      const method = prevLiked ? 'DELETE' : 'POST';
      const res = await apiFetch(likePath, { method, cache: 'no-store' });
      if (res.status === 401) {
        // Revert and prompt login with returnTo
        setLiked(prevLiked);
        setLikes(prevLikes);
        try {
          const dest = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
          window.location.href = `/login?returnTo=${encodeURIComponent(dest)}`;
        } catch {}
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json().catch(() => null);
      if (json && typeof json === 'object') {
        if (typeof (json as any).likeCount === 'number') setLikes((json as any).likeCount);
        if (typeof (json as any).isLikedByMe === 'boolean') setLiked((json as any).isLikedByMe);
      }
    } catch (e: any) {
      // revert on failure
      setLiked(prevLiked);
      setLikes(prevLikes);
      toast({ title: 'Failed to update like', description: e?.message || 'Please try again', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }, [API_BASE, likePath, liked, likes, busy, toast]);

  const onReport = useCallback(async () => {
    if (!reportPath || busy) return;
    setBusy(true);
    try {
      const res = await apiFetch(reportPath, { method: 'POST', cache: 'no-store' });
      if (res.status !== 202 && !res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: 'Report received', description: 'Thanks for letting us know.', variant: 'info' });
    } catch (e: any) {
      toast({ title: 'Failed to report', description: e?.message || 'Please try again', variant: 'destructive' });
    } finally { setBusy(false); }
  }, [API_BASE, reportPath, busy, toast]);

  function Actions() {
    const canLike = typeof liked !== 'undefined';
    return (
      <div className="flex items-center gap-3 px-4 pb-3 pt-2 text-xs text-muted-foreground">
        <button
          onClick={toggleLike}
          className="px-2 py-1 rounded border hover:bg-accent disabled:opacity-50"
          aria-pressed={!!liked}
          disabled={!canLike || busy || !authed}
          title={authed ? (liked ? 'Unlike' : 'Like') : 'Log in to like'}
        >
          {liked ? '♥ Liked' : '♡ Like'} {typeof likes === 'number' ? likes : ''}
        </button>
        <button
          onClick={onReport}
          className="px-2 py-1 rounded border hover:bg-accent disabled:opacity-50"
          disabled={busy}
        >
          Report
        </button>
      </div>
    );
  }

  // Split conditional branches into subcomponents so their hooks are not conditional
  function ImageFeedItemCard({ i }: { i: ImageItem }) {
    const created = (item as any)?.createdAt ? new Date((item as any).createdAt) : null;
    // Authenticated users see the original; guests see the _blurred variant
    const rawSrc = authed ? unblurUrl(String(i.src || '')) : String(i.src || '');
    const full = toAbsoluteMediaUrl(rawSrc || undefined);
    const candidates = (() => {
      const raw = rawSrc.trim();
      if (!raw) return [] as string[];
      const out: string[] = [];
      const isAbs = /^https?:\/\//i.test(raw);
      let pathOnly = raw;
      if (isAbs) {
        try { const u = new URL(raw); pathOnly = u.pathname.replace(/^\/+/, ''); } catch { pathOnly = raw.replace(/^https?:\/\/[^/]+\//i, '').replace(/^\/+/, ''); }
      } else {
        pathOnly = raw.replace(/^\/+/, '');
      }
      if (isAbs) out.push(raw);
      out.push(toAbsoluteMediaUrl(pathOnly));
      out.push(`/${pathOnly}`);
      return Array.from(new Set(out.filter(Boolean)));
    })();
    const [idx, setIdx] = React.useState(0);
    const src = candidates[idx] || '';
    const prefetchFull = () => {
      if (!full) return;
      try {
        if (typeof window !== 'undefined' && (window as any).Image) {
          const im: any = new (window as any).Image();
          im.decoding = 'async';
          im.src = full;
        }
      } catch {}
    };
    return (
      <Card className="overflow-hidden relative shadow-sm">
        <button
          type="button"
          className="group block w-full text-left"
          onClick={() => full && onOpenImage?.(full, i.title || 'Image', i.description || i.title || undefined)}
          onMouseEnter={prefetchFull}
          onFocus={prefetchFull}
        >
          <div className="relative aspect-[4/3] bg-muted">
            {src ? (
              <Image
                src={src}
                alt={i.title || 'Image'}
                fill
                className="object-cover"
                sizes="(min-width: 1280px) 30vw, (min-width: 768px) 45vw, 90vw"
                onError={() => setIdx(j => Math.min(j + 1, Math.max(0, candidates.length - 1)))}
                priority={false}
              />
            ) : null}
            <BlurGate unsafe={i.isUnsafeContent} />
          </div>
          {(i.title || i.description) && (
            <CardContent className="p-4">
              {i.title && <div className="text-sm font-medium line-clamp-2">{scrubContactInfo(i.title)}</div>}
              {i.description && <div className="text-xs text-muted-foreground line-clamp-2">{scrubContactInfo(i.description)}</div>}
            </CardContent>
          )}
        </button>
        {created ? (
          <div className="px-4 pt-1 text-[11px] text-muted-foreground">{created.toLocaleDateString()}</div>
        ) : null}
        <Actions />
      </Card>
    );
  }

  function PostFeedItemCard({ p }: { p: PostItem }) {
    const thumbCandidates = (() => {
      const raw = String(p.thumbnailUrl || '').trim();
      if (!raw) return [] as string[];
      const out: string[] = [];
      const isAbs = /^https?:\/\//i.test(raw);
      let pathOnly = raw;
      if (isAbs) {
        try { const u = new URL(raw); pathOnly = u.pathname.replace(/^\/+/, ''); } catch { pathOnly = raw.replace(/^https?:\/\/[^/]+\//i, '').replace(/^\/+/, ''); }
      } else {
        pathOnly = raw.replace(/^\/+/, '');
      }
      if (isAbs) out.push(raw);
      out.push(toAbsoluteMediaUrl(pathOnly));
      out.push(`/${pathOnly}`);
      return Array.from(new Set(out.filter(Boolean)));
    })();
    const [pIdx, setPIdx] = React.useState(0);
    const thumb = thumbCandidates[pIdx] || '';
    const created = (item as any)?.createdAt ? new Date((item as any).createdAt) : null;
    return (
      <Link href={`/posts/${encodeURIComponent(String(p.id))}`} className="block">
        <Card className="overflow-hidden relative shadow-sm">
          {thumb ? (
            <div className="relative w-full aspect-[4/3]">
              <Image
                src={thumb}
                alt={p.title || 'Post thumbnail'}
                fill
                className="object-cover"
                sizes="(min-width: 1280px) 30vw, (min-width: 768px) 45vw, 90vw"
                onError={() => setPIdx(j => Math.min(j + 1, Math.max(0, thumbCandidates.length - 1)))}
                priority={false}
              />
              <BlurGate unsafe={p.isUnsafeContent} />
            </div>
          ) : null}
          <CardContent className="p-4">
            <div className="text-sm font-semibold line-clamp-2">{scrubContactInfo(p.title || 'Post')}</div>
            {p.snippet && <div className="text-xs text-muted-foreground line-clamp-2">{scrubContactInfo(p.snippet)}</div>}
            {created ? (<div className="mt-1 text-[11px] text-muted-foreground">{created.toLocaleDateString()}</div>) : null}
          </CardContent>
          <Actions />
        </Card>
      </Link>
    );
  }

  // Render types
  if (item.type === 'video') {
    const v = item as VideoItem;
    const created = (item as any)?.createdAt ? new Date((item as any).createdAt) : null;
    return (
      <div className="relative">
        <VideoGalleryCard id={Number(v.id)} title={v.title || 'Video'} thumbnailUrl={authed ? unblurUrl(String(v.thumbnailUrl || '')) : String(v.thumbnailUrl || '')} viewCount={v.viewCount ?? null} durationSeconds={v.durationSeconds ?? null} unsafe={!!v.isUnsafeContent} />
        <BlurGate unsafe={v.isUnsafeContent} />
        {created ? (
          <div className="px-4 pt-2 text-[11px] text-muted-foreground">{created.toLocaleDateString()}</div>
        ) : null}
        <Actions />
      </div>
    );
  }

  if (item.type === 'image') {
    return <ImageFeedItemCard i={item as ImageItem} />;
  }

  if (item.type === 'post') {
    return <PostFeedItemCard p={item as PostItem} />;
  }

  return null;
}
